<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Client Management</h5>
        <p class="page-sub">Clients are linked to shifts and used for location-based clock-in verification</p>
      </div>
      <div class="page-actions">
        <button v-if="permission?.manage_client" class="btn-primary-action" @click="openCreate">
          <font-awesome-icon icon="plus" /> New Client
        </button>
      </div>
    </div>

    <!-- Search & filter bar -->
    <div class="list-toolbar">
      <div class="search-wrap">
        <font-awesome-icon icon="search" class="search-icon" />
        <input class="search-input" type="text" v-model="search" placeholder="Search clients…" @input="debouncedFetch" />
      </div>
      <select class="filter-select" v-model="filterLocation" @change="fetchClients(1)">
        <option :value="null">All Locations</option>
        <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
      </select>
      <select class="filter-select" v-model="filterCategory" @change="fetchClients(1)">
        <option :value="null">All Categories</option>
        <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
      </select>
    </div>

    <!-- Table skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Client Name</th>
            <th>Category</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Address</th>
            <th>Location</th>
            <th class="th-coords">Coordinates</th>
            <th class="th-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in clients" :key="client.id">
            <td class="td-name">{{ client.customer_name }}</td>
            <td>
              <span v-if="client.category" class="cat-chip" :style="client.category.color ? `background:${client.category.color}22;color:${client.category.color}` : ''">
                {{ client.category.name }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ client.phone_no || '—' }}</td>
            <td>{{ client.email || '—' }}</td>
            <td class="td-addr">{{ client.address || '—' }}</td>
            <td>{{ client.location?.name || '—' }}</td>
            <td class="td-coords">
              <span v-if="client.latitude && client.longitude" class="coords-chip">
                <font-awesome-icon icon="map-marker-alt" />
                {{ parseFloat(client.latitude).toFixed(5) }}, {{ parseFloat(client.longitude).toFixed(5) }}
              </span>
              <span v-else class="coords-chip coords-chip--none">No coordinates</span>
            </td>
            <td class="td-actions">
              <button v-if="permission?.manage_client" class="btn-icon" title="Edit" @click="openEdit(client)"><font-awesome-icon icon="pen" /></button>
              <button v-if="permission?.manage_client" class="btn-icon btn-icon--red" title="Delete" @click="confirmDelete(client)"><font-awesome-icon icon="trash" /></button>
            </td>
          </tr>
          <tr v-if="!clients.length">
            <td colspan="8" class="empty-row">
              <div class="empty-state">
                <font-awesome-icon icon="building" class="empty-icon" />
                <p>No clients yet</p>
                <button v-if="permission?.manage_client" class="btn-primary-action" @click="openCreate">Add First Client</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination-row" v-if="meta.last_page > 1">
        <button class="page-btn" :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
          <font-awesome-icon icon="chevron-left" />
        </button>
        <span class="page-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
          <font-awesome-icon icon="chevron-right" />
        </button>
      </div>
    </div>

    <!-- ══ Create / Edit Modal ══ -->
    <div class="modal-overlay" v-if="modal.open" @click.self="modal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">{{ modal.id ? 'Edit Client' : 'New Client' }}</p>
          <button class="modal-close" @click="modal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">

          <div class="form-row">
            <label class="form-label">Client Name <span class="req">*</span></label>
            <input type="text" class="form-input" v-model="modal.customer_name" placeholder="e.g. Acme Corporation" />
          </div>

          <div class="form-row">
            <label class="form-label">Category</label>
            <select class="form-input" v-model="modal.category_id">
              <option :value="null">— No category —</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>

          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Phone</label>
              <input type="text" class="form-input" v-model="modal.phone_no" placeholder="+44 7700 000000" />
            </div>
            <div class="form-row">
              <label class="form-label">Email</label>
              <input type="email" class="form-input" v-model="modal.email" placeholder="contact@client.com" />
            </div>
          </div>

          <div class="form-row">
            <label class="form-label">Address</label>
            <input type="text" class="form-input" v-model="modal.address" placeholder="123 Business Park, London" />
          </div>

          <div class="form-row">
            <label class="form-label">Location</label>
            <select class="form-input" v-model="modal.location_id">
              <option :value="null">— No location —</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>

          <!-- Coordinates section -->
          <div class="coords-section">
            <div class="coords-header">
              <font-awesome-icon icon="map-marker-alt" class="coords-header-icon" />
              <div>
                <p class="coords-title">Site Coordinates</p>
                <p class="coords-sub">Used for shift clock-in location verification</p>
              </div>
              <button type="button" class="btn-gps" @click="captureGps" :disabled="gpsLoading">
                <font-awesome-icon :icon="gpsLoading ? 'spinner' : 'crosshairs'" :spin="gpsLoading" />
                {{ gpsLoading ? 'Getting…' : 'Use GPS' }}
              </button>
            </div>
            <p v-if="gpsError" class="gps-error">{{ gpsError }}</p>

            <div class="form-cols">
              <div class="form-row">
                <label class="form-label">Latitude</label>
                <input type="number" step="0.0000001" class="form-input" v-model="modal.latitude" placeholder="e.g. 51.5074" />
              </div>
              <div class="form-row">
                <label class="form-label">Longitude</label>
                <input type="number" step="0.0000001" class="form-input" v-model="modal.longitude" placeholder="e.g. -0.1278" />
              </div>
            </div>

            <!-- Live map preview link -->
            <a
              v-if="modal.latitude && modal.longitude"
              :href="`https://www.openstreetmap.org/?mlat=${modal.latitude}&mlon=${modal.longitude}&zoom=16`"
              target="_blank"
              rel="noopener"
              class="map-preview-link"
            >
              <font-awesome-icon icon="external-link-alt" /> View on map
            </a>
          </div>

          <!-- is_client flag note -->
          <div class="flag-note">
            <font-awesome-icon icon="check-circle" class="flag-icon" />
            This record will be flagged as a <strong>Client</strong> and available for shift scheduling
          </div>

        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="modal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : (modal.id ? 'Update Client' : 'Create Client') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';

export default {
  name: 'HrClients',
  data() {
    return {
      toast:      useToast(),
      store:      useStore(),
      loading:    false,
      saving:     false,
      gpsLoading: false,
      gpsError:   '',
      clients:        [],
      locations:      [],
      categories:     [],
      meta:           { current_page: 1, last_page: 1 },
      search:          '',
      filterLocation:  null,
      filterCategory:  null,
      searchTimer:     null,
      modal: {
        open: false, id: null,
        customer_name: '', phone_no: '', email: '', address: '',
        latitude: '', longitude: '', location_id: null, category_id: null,
      },
    };
  },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
  },
  mounted() {
    this.fetchClients();
    this.fetchLocations();
    this.fetchCategories();
  },
  methods: {
    async fetchClients(page = 1) {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/clients', {
          params: {
            search:      this.search         || undefined,
            location_id: this.filterLocation || undefined,
            category_id: this.filterCategory || undefined,
            length: 20,
            page,
          },
        });
        this.clients = data.data?.data || [];
        this.meta    = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to load clients'); }
      finally { this.loading = false; }
    },

    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data ?? [];
      } catch { /* optional */ }
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/hr/clients/categories');
        this.categories = data.data ?? [];
      } catch { /* optional */ }
    },

    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchClients(), 300);
    },

    changePage(page) { this.fetchClients(page); },

    openCreate() {
      this.modal = { open: true, id: null, customer_name: '', phone_no: '', email: '', address: '', latitude: '', longitude: '', location_id: null, category_id: null };
      this.gpsError = '';
    },

    openEdit(client) {
      this.modal = {
        open:          true,
        id:            client.id,
        customer_name: client.customer_name,
        phone_no:      client.phone_no  || '',
        email:         client.email     || '',
        address:       client.address   || '',
        latitude:      client.latitude    || '',
        longitude:     client.longitude   || '',
        location_id:   client.location_id  || null,
        category_id:   client.category_id  || null,
      };
      this.gpsError = '';
    },

    async captureGps() {
      if (!navigator.geolocation) {
        this.gpsError = 'Geolocation is not supported by your browser'; return;
      }
      this.gpsLoading = true;
      this.gpsError   = '';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.modal.latitude  = parseFloat(pos.coords.latitude.toFixed(7));
          this.modal.longitude = parseFloat(pos.coords.longitude.toFixed(7));
          this.gpsLoading = false;
        },
        () => {
          this.gpsError   = 'Could not get location. Please enter coordinates manually.';
          this.gpsLoading = false;
        },
        { timeout: 8000 }
      );
    },

    async save() {
      if (!this.modal.customer_name.trim()) {
        this.toast.error('Client name is required'); return;
      }
      this.saving = true;
      try {
        const payload = {
          customer_name: this.modal.customer_name.trim(),
          phone_no:      this.modal.phone_no  || null,
          email:         this.modal.email     || null,
          address:       this.modal.address   || null,
          latitude:      this.modal.latitude    !== '' ? this.modal.latitude    : null,
          longitude:     this.modal.longitude   !== '' ? this.modal.longitude   : null,
          location_id:   this.modal.location_id  || null,
          category_id:   this.modal.category_id  || null,
        };
        if (this.modal.id) {
          await axios.put(`/hr/clients/${this.modal.id}`, payload);
          this.toast.success('Client updated');
        } else {
          await axios.post('/hr/clients', payload);
          this.toast.success('Client created');
        }
        this.modal.open = false;
        this.fetchClients();
      } catch (e) {
        const msg = e.response?.data?.message;
        this.toast.error(typeof msg === 'string' ? msg : 'Save failed');
      } finally { this.saving = false; }
    },

    async confirmDelete(client) {
      if (!confirm(`Delete client "${client.customer_name}"? This cannot be undone.`)) return;
      try {
        await axios.delete(`/hr/clients/${client.id}`);
        this.toast.success('Client deleted');
        this.fetchClients();
      } catch (e) {
        const msg = e.response?.data?.message;
        this.toast.error(typeof msg === 'string' ? msg : 'Delete failed');
      }
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-outline:hover { background:#f5f7fa; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:13px; transition:color .15s,background .15s; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }

/* Toolbar */
.list-toolbar  { display:flex; align-items:center; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.search-wrap   { display:flex; align-items:center; gap:8px; background:#fff; border:1px solid #dde5f0; border-radius:8px; padding:0 12px; height:38px; flex:1; max-width:360px; }
.search-icon   { color:#aab8cc; font-size:13px; }
.search-input  { border:none; outline:none; font-size:13px; color:#1a2b45; flex:1; background:transparent; }
.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }

/* Skeleton */
.sk-table { display:flex; flex-direction:column; gap:8px; }
.sk-row   { height:48px; border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Table */
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table th { padding:12px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tr:last-child td { border-bottom:none; }
.data-table tr:hover td { background:#fafbfd; }
.th-coords,.td-coords { width:200px; }
.th-actions,.td-actions { width:80px; text-align:right; }
.td-name  { font-weight:600; }
.td-addr  { max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.td-actions { display:flex; justify-content:flex-end; gap:2px; }

.coords-chip       { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:3px 8px; border-radius:6px; background:#e0efff; color:#004AAD; white-space:nowrap; }
.coords-chip--none { background:#f1f5f9; color:#94a3b8; }
.cat-chip   { display:inline-block; font-size:11px; font-weight:600; padding:2px 9px; border-radius:20px; background:#f1f5f9; color:#4a5e78; white-space:nowrap; }
.text-muted { color:#94a3b8; font-size:13px; }

.empty-row  { padding:0 !important; }
.empty-state { display:flex; flex-direction:column; align-items:center; gap:12px; padding:60px 20px; color:#7a90b0; }
.empty-icon  { font-size:44px; opacity:.3; }

/* Pagination */
.pagination-row { display:flex; align-items:center; justify-content:center; gap:10px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn  { width:32px; height:32px; border-radius:8px; border:1px solid #dde5f0; background:#fff; cursor:pointer; color:#1a2b45; display:flex; align-items:center; justify-content:center; font-size:11px; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info { font-size:12px; color:#7a90b0; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:560px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-cols     { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }

/* Coordinates section */
.coords-section { border:1px solid #e4eaf2; border-radius:10px; padding:14px 16px; display:flex; flex-direction:column; gap:12px; background:#f8fafc; }
.coords-header  { display:flex; align-items:flex-start; gap:10px; }
.coords-header-icon { font-size:16px; color:#004AAD; margin-top:3px; flex-shrink:0; }
.coords-title   { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.coords-sub     { font-size:11px; color:#7a90b0; margin:0; }
.btn-gps {
  margin-left:auto; flex-shrink:0; display:inline-flex; align-items:center; gap:6px;
  height:32px; padding:0 12px; border-radius:7px; border:1px solid #bcd4f6;
  background:#e0efff; color:#004AAD; font-size:12px; font-weight:600; cursor:pointer;
}
.btn-gps:hover:not(:disabled) { background:#c8e0f8; }
.btn-gps:disabled { opacity:.6; cursor:not-allowed; }
.gps-error { font-size:11px; color:#dc2626; margin:0; }
.map-preview-link { font-size:12px; color:#004AAD; text-decoration:none; display:inline-flex; align-items:center; gap:5px; }
.map-preview-link:hover { text-decoration:underline; }

/* is_client flag note */
.flag-note { display:flex; align-items:center; gap:8px; padding:10px 12px; border-radius:8px; background:#dcfce7; font-size:12px; color:#166534; }
.flag-icon { color:#16a34a; }
</style>
